import { z } from "zod"
import { IdentificationType } from "./identityType"
import { parseRsaIdDob } from "../utils/rsaId"

export const employeeProfileSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    secondName: z.string().optional(),
    lastName: z.string().min(1, "Last name is required"),
    idType: z.string().min(1, "Identification type is required"),
    idNumber: z.string().min(1, "Identification number is required"),
    gender: z.string().min(1, "Gender is required"),
    dateOfBirth: z.string().min(1, "Date of birth is required"),
    phone: z
      .string()
      .min(1, "Phone number is required")
      .regex(/^\d+$/, "Phone number must contain only digits"),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Invalid email address"),
    position: z.string().min(1, "Position is required"),
    taxNumber: z.string().min(1, "Tax number is required"),
    raceGroup: z.string().min(1, "Race group is required"),
    maritalStatus: z.string().min(1, "Marital status is required"),
    emergencyContactName: z
      .string()
      .min(1, "Emergency contact name is required"),
    emergencyContactPhone: z
      .string()
      .min(1, "Emergency contact phone is required")
      .regex(/^\d+$/, "Phone number must contain only digits"),
  })
  .superRefine((data, ctx) => {
    if (data.idType === IdentificationType.NationalId) {
      if (!/^\d+$/.test(data.idNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ID number must contain only digits",
          path: ["idNumber"],
        })
        return
      }
      if (data.idNumber.length >= 6) {
        const expectedDob = parseRsaIdDob(data.idNumber)
        if (expectedDob !== null && data.dateOfBirth !== expectedDob) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Date of birth must match first 6 digits of your RSA ID",
            path: ["dateOfBirth"],
          })
        }
      }
    }
  })

export const addressSchema = z.object({
  line1: z.string().min(1, "Street address is required"),
  line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  postalCode: z
    .string()
    .min(1, "Postal code is required")
    .regex(/^\d+$/, "Postal code must contain only digits"),
  country: z.string().min(1, "Country is required"),
  complexNumber: z.string().optional(),
  complexName: z.string().optional(),
})

export const bankDetailsSchema = z.object({
  accountHolderName: z.string().min(1, "Account holder name is required"),
  bankName: z.string().min(1, "Bank name is required"),
  accountNumber: z
    .string()
    .min(1, "Account number is required")
    .regex(/^\d+$/, "Account number must contain only digits"),
  branchCode: z.string().min(1, "Branch code is required"),
  accountType: z.string().min(1, "Account type is required"),
  accountRelationship: z.string().min(1, "Account relationship is required"),
})

export const documentsSchema = z.object({
  idProof: z.instanceof(File, { message: "ID proof is required" }),
  addressProof: z.instanceof(File, { message: "Address proof is required" }),
  bankStatement: z.instanceof(File, { message: "Bank statement is required" }),
})

export type EmployeePersonalDetailsFormData = z.infer<
  typeof employeeProfileSchema
>
export type AddressFormData = z.infer<typeof addressSchema>
export type BankDetailsFormData = z.infer<typeof bankDetailsSchema>
export type DocumentsFormData = z.infer<typeof documentsSchema>
